import type { Player } from '../shared/entities.ts';
import type { Vector } from '../shared/vector.ts';
import { movePlayer } from '../shared/world.ts';

/** A frame this end played on its own, kept until the server has caught up. */
interface Step {
  /** The instant it started at, on the clock this file keeps. */
  at: number;
  duration: number;
  player: Player;
}

/**
 * The frames this end has played for its own player that the server has not
 * answered for yet, and the clock they are counted in.
 *
 * The clock is milliseconds since this end asked to join, and every order it
 * gives carries the instant it was given at. The server anchors its answer to
 * that instant and says which point of *this* clock its word reaches, so the
 * correction stops being a comparison of two distances — which cannot tell a
 * prediction running ahead from a dash one end took and the other refused,
 * both of them around a hundred pixels — and becomes a rewind: plant the
 * player where the server put it and play the rest again.
 *
 * The clock is an argument only because a test drives one that does not tick.
 */
export function createPrediction(now: () => number = () => performance.now()) {
  const journal: Step[] = [];
  let joinedAt = now();

  return { stamp, join, predict, reconcile };

  /** The instant an order is given at, which is what the server anchors to. */
  function stamp() {
    return now() - joinedAt;
  }

  /**
   * A new life starts a new clock, and the server starts counting from zero
   * for it too: the join is the only message both ends can date the same.
   */
  function join() {
    journal.length = 0;
    joinedAt = now();
  }

  /** One frame played without waiting for the round trip, and written down. */
  function predict(player: Player, duration: number) {
    if (canMove(player)) {
      journal.push({ at: stamp() - duration, duration, player: copy(player) });
    }

    movePlayer(player, duration);
  }

  /**
   * Plant the player on the server's word and play again every frame the word
   * did not include. The countdowns are not replayed: this end owns them, it
   * has been counting them down all along, and running them again here would
   * empty a clip at double speed.
   */
  function reconcile(player: Player, said: Vector, applied: number) {
    while (journal.length && ends(journal[0]) <= applied) journal.shift();

    let position = said;

    for (const step of journal) {
      position = movedFrom(position, step, Math.max(0, applied - step.at));
    }

    player.position = position;
  }
}

/** A step that could not have moved anybody is nothing to play again. */
function canMove({ input, dash }: Player) {
  return dash > 0 || input.up || input.down || input.left || input.right;
}

/** The player as it was when the frame was played, and nobody else's to touch. */
function copy(player: Player): Player {
  return {
    ...player,
    input: { ...player.input, aim: { ...player.input.aim } },
  };
}

function ends(step: Step) {
  return step.at + step.duration;
}

/**
 * The same frame from somewhere else, minus the part of it the server has
 * already seen — and the shove has that much less left to give. Only the
 * position is read back.
 */
function movedFrom(position: Vector, step: Step, seen: number) {
  const { player, duration } = step;
  const ghost = { ...player, position, dash: Math.max(0, player.dash - seen) };

  movePlayer(ghost, duration - seen);
  return ghost.position;
}
